import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { AlertTriangle, CheckCircle2, ExternalLink, FilePlus2, FileText, Loader2, Plus } from 'lucide-react';
import { Layout } from '@/components/layout';
import { useAuthStore } from '@/lib/auth-store';
import { useGetMe, getGetMeQueryKey } from '@workspace/api-client-react';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { poDraftImportApi, type PoAddress, type PoCreatedDraft, type PoCustomer, type PoReview } from '@/lib/po-draft-import-api';
import { addressErrors, addressIsBlank, emptyAddress, formatOrderDate, lineSubtotal, money, normalizePo, qtyValid, type DraftLine } from '@/lib/po-draft-utils';
import { UploadZone } from '@/components/po-draft/upload-zone';
import { CustomerPicker } from '@/components/po-draft/customer-picker';
import { AddressEditor } from '@/components/po-draft/address-editor';
import { LineEditor } from '@/components/po-draft/line-editor';
import { MappingManager, useMappings } from '@/components/po-draft/mapping-manager';
import { SaveMappingsPanel } from '@/components/po-draft/save-mappings-panel';
import { ConfirmDialog } from '@/components/po-draft/confirm-dialog';
import { PoPreview } from '@/components/po-draft/po-preview';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
let keySeq = 0;

export function PoDraftImport() {
  const token = useAuthStore((s) => s.token) ?? '';
  const [, setLocation] = useLocation();
  const { data: me, isLoading: meLoading } = useGetMe({
    request: { headers: { Authorization: `Bearer ${token}` } },
    query: { queryKey: [...getGetMeQueryKey(), token], enabled: !!token, retry: false },
  });
  useEffect(() => {
    if (!meLoading && me && !(me as any).featureFlags?.purchase_orders) setLocation('/');
  }, [meLoading, me, setLocation]);

  const [stores, setStores] = useState<{ id: string; nickname: string; storeUrl: string }[]>([]);
  const [storeId, setStoreId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState('');
  const [review, setReview] = useState<PoReview | null>(null);
  const [poRaw, setPoRaw] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [customer, setCustomer] = useState<PoCustomer | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [address, setAddress] = useState<PoAddress>(emptyAddress());
  const [showConfirm, setShowConfirm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [created, setCreated] = useState<PoCreatedDraft | null>(null);
  const [mappingSaving, setMappingSaving] = useState(false);
  const mappings = useMappings(storeId, token);
  const abortRef = useRef<AbortController | null>(null);
  const generation = useRef(0);

  useEffect(() => {
    if (!token) return;
    fetch(`${BASE}/api/stores`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((j) => setStores(Array.isArray(j) ? j : j.stores ?? [])).catch(() => setStores([]));
  }, [token]);

  const reset = useCallback(() => {
    generation.current++;
    abortRef.current?.abort(); abortRef.current = null;
    setFile(null); setExtracting(false); setExtractError(''); setReview(null); setPoRaw(''); setLines([]);
    setPreviewOpen(false);
    setCustomer(null); setConfirmed(false); setAddress(emptyAddress()); setShowConfirm(false);
    setCreating(false); setMappingSaving(false); setCreateError(''); setCreated(null);
  }, []);
  useEffect(() => { reset(); }, [storeId, token, reset]);
  useEffect(() => () => abortRef.current?.abort(), []);

  const extract = async (f: File) => {
    if (!storeId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController(); abortRef.current = ctrl;
    const gen = ++generation.current;
    setFile(f); setExtracting(true); setExtractError('');
    setPreviewOpen(false);
    try {
      const r = await poDraftImportApi.extract(storeId, f, token, ctrl.signal);
      if (ctrl.signal.aborted || gen !== generation.current) return;
      const ex = r.extracted;
      setReview(r); setPoRaw(normalizePo(ex.poNumber)); setAddress({ ...emptyAddress(), ...ex.shippingAddress });
      setCustomer(r.presetCustomer ?? null); setConfirmed(!!r.presetCustomer);
      setLines(ex.lines.map((l, i) => {
        const m = r.lineMatches.find((x) => x.index === i);
        const cands = m?.candidates ?? [];
        const saved = r.presetCustomer && m?.savedMappingStatus === 'available'
          ? cands.find(c => c.id === m.savedVariantId && c.productStatus !== 'ARCHIVED') : undefined;
        return { key: `l${++keySeq}`, sku: l.sku, title: l.title, quantity: String(l.quantity), pdfPrice: l.pdfUnitPrice, attributes: l.attributes.map((a) => ({ ...a })),
          candidates: cands, exactSku: !!m?.exactSku, manual: false, variant: saved ?? (!m?.savedMappingStatus && m?.exactSku && cands.length === 1 && cands[0]!.productStatus !== 'ARCHIVED' ? cands[0]! : null),
          savedVariantId: m?.savedVariantId, savedStatus: m?.savedMappingStatus };
      }));
    } catch (e: any) {
      if (ctrl.signal.aborted || gen !== generation.current) return;
      setExtractError(e.message || 'Extraction failed');
    } finally {
      if (gen === generation.current) setExtracting(false);
    }
  };

  const po = normalizePo(poRaw);
  const reference = `#${po}`;
  const currency = review?.currency ?? 'AUD';
  const addrErrs = addressErrors(address);
  const blockers = useMemo(() => {
    const b: string[] = [];
    if (!po || /^PO$/i.test(po) || !/^[A-Za-z0-9][A-Za-z0-9._/-]{0,99}$/.test(po)) b.push('Enter the order number only, up to 100 characters, without spaces or a PO label');
    if (!customer || !confirmed) b.push('Select and confirm a billing customer');
    if (!lines.length) b.push('Add at least one line');
    if (lines.some((l) => !l.variant || l.variant.productStatus === 'ARCHIVED')) b.push('Choose an available variant for every line');
    if (lines.some((l) => !qtyValid(l))) b.push('Quantities must be whole numbers above 0');
    if (lines.some((l) => l.attributes.some((a) => !a.key.trim() && a.value.trim()))) b.push('Attributes need a name');
    if (addrErrs.length) b.push('Complete or clear the shipping address');
    return b;
  }, [po, customer, confirmed, lines, addrErrs.length]);

  const create = async () => {
    if (creating || blockers.length || !customer) return;
    setCreating(true); setCreateError('');
    const gen = generation.current;
    try {
      const res = await poDraftImportApi.create({
        storeId, poNumber: po, customerId: customer.id,
        shippingAddress: Object.fromEntries(Object.entries(addressIsBlank(address) ? emptyAddress() : address).map(([k, v]) => [k, String(v).trim()])) as PoAddress,
        notes: '',
        lines: lines.map((l) => ({ variantId: l.variant!.id, quantity: Number(l.quantity), expectedPrice: l.variant!.price,
          attributes: l.attributes.filter((a) => a.key.trim()).map((a) => ({ key: a.key.trim(), value: a.value })) })),
      }, token);
      if (gen !== generation.current) return;
      setCreated(res); setShowConfirm(false);
    } catch (e: any) {
      if (gen !== generation.current) return;
      setCreateError(e.message || 'Could not create the draft order'); setShowConfirm(false);
    } finally {
      if (gen === generation.current) setCreating(false);
    }
  };

  const busy = extracting || creating || mappingSaving;
  const updateLine = (i: number, l: DraftLine) => setLines((p) => p.map((x, j) => (j === i ? l : x)));
  const ex = review?.extracted;
  const buyerName = ex?.buyer.company || ex?.buyer.name;
  const hideMatches = me?.email?.trim().toLowerCase() === 'info@vizionconcepts.com.au' || !!review?.presetCustomer;

  return (
    <Layout collapseMobileNav>
      <div className="space-y-5 pb-10">
        <div>
          <h2 className="text-xl font-semibold">PDF to Draft Order</h2>
          <p className="text-sm text-muted-foreground">Turn a customer purchase order PDF into a Shopify draft order after you review every match.</p>
        </div>
        <div className="glass-panel p-4 max-w-md">
          <ShopifyTargetStoreSelector stores={stores} value={storeId} onChange={setStoreId} disabled={busy} />
          {stores.length === 0 && <p className="text-sm text-muted-foreground">Connect a Shopify store to get started.</p>}
        </div>

        {storeId && !meLoading && me && !hideMatches && <MappingManager storeId={storeId} token={token} state={mappings} disabled={busy} />}
        {file && <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} data-testid="button-preview-po">
          <FileText className="w-4 h-4 mr-1.5" />Preview PO
        </Button>}

        {created ? (
          <div className="glass-panel p-6 space-y-4 max-w-xl" data-testid="panel-success">
            <div className="flex items-center gap-2 text-primary font-semibold"><CheckCircle2 className="w-5 h-5" />{created.reused ? 'Existing draft order found' : 'Draft order created'}</div>
            <dl className="text-[13px] grid grid-cols-[110px_1fr] gap-y-1.5">
              <dt className="text-muted-foreground">Draft order</dt><dd className="font-mono" data-testid="text-draft-name">{created.name}</dd>
              <dt className="text-muted-foreground">Reference</dt><dd className="font-mono" data-testid="text-draft-reference">{created.reference}</dd>
              {created.discount && <><dt className="text-muted-foreground">Discount</dt><dd data-testid="text-draft-discount">
                {created.discount.title} · {created.discount.valueType === 'PERCENTAGE' ? `${created.discount.value}% off` : money(created.discount.value, currency)}
              </dd></>}
              <dt className="text-muted-foreground">Shipping</dt><dd data-testid="text-draft-shipping">{created.shipping
                ? `${created.shipping.title} · ${money(Number(created.shipping.price), created.shipping.currency)}`
                : 'No shipping rate attached'}</dd>
            </dl>
            {created.reused && <p className="text-xs text-muted-foreground">A draft with this reference already existed, so no duplicate was created.</p>}
            {review?.presetCustomer && (!created.discount || created.discount.title !== 'TRADE' ||
              created.discount.valueType !== 'PERCENTAGE' || created.discount.value !== 20) &&
              <p className="text-xs text-amber-600">Check the TRADE discount in Shopify before sending this existing draft; the 20% discount is not confirmed.</p>}
            {!created.shipping && !addressIsBlank(address) && <p className="text-xs text-amber-600">Check shipping in Shopify before sending this draft; it has no confirmed shipping rate.</p>}
            <p className="text-xs text-muted-foreground">Draft only. No invoice was sent and the order is not completed.</p>
            <div className="flex flex-wrap gap-2">
              <a href={created.url} target="_blank" rel="noopener noreferrer" data-testid="link-shopify-draft"><Button><ExternalLink className="w-4 h-4 mr-1.5" />Open in Shopify</Button></a>
              <Button variant="outline" onClick={reset} data-testid="button-start-another"><FilePlus2 className="w-4 h-4 mr-1.5" />Start another import</Button>
            </div>
          </div>
        ) : !review ? (
          <div className="max-w-2xl">
            <UploadZone disabled={!storeId || creating} extracting={extracting} fileName={file?.name ?? ''} error={extractError}
              onFile={extract} onCancel={reset} onRetry={file ? () => extract(file) : undefined} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-[13px]">
              <span className="text-muted-foreground truncate">Source: {file?.name} (not saved in SoraFlo)</span>
              <Button size="sm" variant="outline" disabled={busy} onClick={reset} data-testid="button-discard">Discard and start over</Button>
            </div>
            {ex!.warnings.length > 0 && (
              <div className="rounded border border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-[13px]" data-testid="panel-warnings">
                <div className="font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" />Check these before creating</div>
                <ul className="list-disc pl-5 mt-1 space-y-0.5">{ex!.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
              </div>
            )}
            <section className="glass-panel p-4 grid md:grid-cols-3 gap-4">
              <label className="space-y-1"><span className="text-xs text-muted-foreground">PO reference</span>
                <Input value={poRaw} disabled={busy} maxLength={100} onChange={(e) => setPoRaw(e.target.value)} className="font-mono" data-testid="input-po-number" />
                <span className="text-[11px] text-muted-foreground">Prefix PO or # is ignored. Extracted: {ex!.poNumber || 'none'}</span></label>
              <div className="space-y-1"><span className="text-xs text-muted-foreground">Order date</span>
                <div className="text-sm font-mono h-9 flex items-center" data-testid="text-order-date">{formatOrderDate(ex!.orderDate) || 'Not found'}</div></div>
              <div className="space-y-1"><span className="text-xs text-muted-foreground">Issuer / buyer on PDF</span>
                <div className="text-sm h-9 flex items-center truncate">{buyerName || 'Not found'}</div></div>
              <div className="md:col-span-3 text-xs rounded bg-muted/50 px-3 py-2" data-testid="text-reference-preview">
                Shopify note and tag will both read only <span className="font-mono font-semibold">{reference}</span>.
              </div>
            </section>
            <div className="grid lg:grid-cols-2 gap-4 items-start">
              {review.presetCustomer ? <section className="glass-panel p-4 space-y-2" data-testid="section-preset-customer">
                <h3 className="text-sm font-semibold">Billing customer</h3>
                <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="w-4 h-4 text-primary" />{review.presetCustomer.name}</div>
                <p className="text-xs text-muted-foreground">Automatically selected and confirmed for your account. Review the PDF details below, then create the draft.</p>
              </section> : <CustomerPicker buyer={ex!.buyer} candidates={review.customerCandidates} storeId={storeId} token={token} selected={customer} confirmed={confirmed}
                disabled={busy} savedStatus={review.savedCustomerStatus}
                savedCustomer={review.savedCustomerId ? (review.customerCandidates.find((c) => c.id === review.savedCustomerId) ?? mappings.items.find((m) => m.kind === 'buyer' && m.targetId === review.savedCustomerId)?.customer ?? null) : null}
                onSelect={(c) => { setCustomer(c); setConfirmed(false); }} onConfirm={() => setConfirmed(true)} />}
              <AddressEditor value={address} onChange={setAddress} disabled={busy} />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">Lines ({lines.length})</h3>
                <Button size="sm" variant="outline" disabled={busy || lines.length >= 200} data-testid="button-add-line"
                  onClick={() => setLines((p) => [...p, { key: `l${++keySeq}`, sku: '', title: '', quantity: '1', pdfPrice: null, attributes: [], variant: null, candidates: [], exactSku: false, manual: true }])}>
                  <Plus className="w-3.5 h-3.5 mr-1" />Add line</Button></div>
              {lines.length === 0 && <div className="glass-panel p-6 text-center text-sm text-muted-foreground" data-testid="text-no-lines">No lines. Add a line manually to continue.</div>}
              {lines.map((l, i) => (
                <LineEditor key={l.key} line={l} savedVariant={l.savedVariantId ? mappings.items.find((m) => m.kind === 'sku' && m.targetId === l.savedVariantId)?.variant ?? null : null} index={i} currency={currency} storeId={storeId} token={token} disabled={busy}
                  onChange={(n) => updateLine(i, n)} onRemove={() => setLines((p) => p.filter((_, j) => j !== i))} />
              ))}
            </div>
            {!hideMatches && !meLoading && me && <SaveMappingsPanel key={`${storeId}:${token}`} storeId={storeId} token={token} buyerKey={review.buyerKey ?? ''} customer={customer} confirmed={confirmed} lines={lines} disabled={busy} onSaved={mappings.reload} onBusy={setMappingSaving} />}
            {createError && <div className="rounded border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px] text-destructive" data-testid="text-create-error">{createError}. Your review is preserved; you can try again.</div>}
            <div className="glass-panel p-4 flex flex-wrap items-center gap-4 sticky bottom-0">
              <div className="flex-1 min-w-[220px] text-[13px]">
                <div>Subtotal <span className="font-mono font-semibold" data-testid="text-subtotal">{money(lineSubtotal(lines), currency)}</span> <span className="text-xs text-muted-foreground">current Shopify prices, excl. tax and shipping</span></div>
                {review.presetCustomer && <p className="text-xs font-medium" data-testid="text-trade-discount">TRADE · 20% off the product subtotal, applied automatically.</p>}
                {!addressIsBlank(address) && <p className="text-xs text-muted-foreground">The cheapest available shipping rate for this address will be added automatically.</p>}
                {blockers.length > 0 && <div className="text-xs text-amber-600" data-testid="text-blockers">{blockers.join(' · ')}</div>}
              </div>
              <Button disabled={busy || blockers.length > 0} onClick={() => review.presetCustomer ? create() : setShowConfirm(true)} data-testid="button-review-create">
                {creating && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Review and create draft</Button>
            </div>
          </div>
        )}
      </div>
      <ConfirmDialog open={showConfirm} busy={creating} onCancel={() => setShowConfirm(false)} onConfirm={create}
        customer={customer} reference={reference} lines={lines} currency={currency} address={address} />
      <PoPreview file={file} open={previewOpen} onOpenChange={setPreviewOpen} />
    </Layout>
  );
}

export default PoDraftImport;
